import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@ds';
import { ChildForm } from '@ui';
import { DonorProfileForm, uploadPendingDocuments } from '@features/forms/DonorProfileForm';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { useAuthContext } from '@lp/core/auth';
import { IoClose } from 'react-icons/io5';
import { CaseStatus, DonorData, DonorType, OrganType } from '@/api/apis/case';
import { retrieveUnifiedError } from '@/api/utils';
import { useUploadFile } from '@/hooks/useUploadFile';
import { CaseDetailsPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { useUpdateCaseMutation, useUpdateOpoCaseDonorDataMutation } from '@/queries/case';
import { focusClosestDialog, isAllowedStatus } from '@/utils/helpers';
import { OpoDonorDataView } from './components/OpoDonorDataView';
import { EditDonorProfileFormValues, editDonorProfileSchema } from './config';
import { mapFormValuesToOpoCaseDonorData, mapOpoCaseDonorDataToFormValues } from './mappers';

type OpoClinicalRequestModalProps = {
  isOpen: boolean;
  onOpenChange: () => void;
  caseId: string;
  organType: OrganType;
  donorType: DonorType;
  status: CaseStatus;
  matchId?: string | null;
  donorData?: DonorData | null;
};

export const OpoClinicalRequestModal = ({
  onOpenChange,
  isOpen,
  caseId,
  organType,
  donorType,
  status,
  matchId,
  donorData,
}: OpoClinicalRequestModalProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const { checkPermission } = useAuthContext();

  const hasEditPermission = checkPermission(CaseDetailsPermissions.OpoDonorDataUpdate);
  const isEditableStatus = isAllowedStatus({
    statusFrom: CaseStatus.Draft,
    statusTo: CaseStatus.ResourcesBooked,
    currentStatus: status,
  });
  const canEdit = hasEditPermission && isEditableStatus;

  const { mutateAsync: updateCaseMutation, isPending: isCasePending } = useUpdateCaseMutation();
  const { mutateAsync: updateDonorData, isPending: isDonorDataPending } = useUpdateOpoCaseDonorDataMutation();
  const { uploadFiles } = useUploadFile(FileType.Case);

  const isPending = isCasePending || isDonorDataPending;

  const defaultValues = mapOpoCaseDonorDataToFormValues({
    matchId: matchId ?? null,
    donorData: donorData ?? null,
    organType,
    donorType,
  });

  const handleOpenChange = () => {
    setIsEditMode(false);
    onOpenChange();
  };

  const handleSubmit = async (values: EditDonorProfileFormValues) => {
    try {
      const nextMatchId = values.matchId?.trim() || null;
      if (nextMatchId !== (matchId || null)) {
        await updateCaseMutation({
          caseId,
          matchId: nextMatchId,
        });
      }
      const ctScanDocuments = await uploadPendingDocuments(values.ctScanDocuments, uploadFiles);
      await updateDonorData({
        caseId,
        ...mapFormValuesToOpoCaseDonorData({ ...values, ctScanDocuments }),
      });
      toast.success('Donor profile updated successfully.');
      setIsEditMode(false);
    } catch (error) {
      const { message } = retrieveUnifiedError(error, 'Failed to update donor profile. Please try again.');
      toast.error(message);
    }
  };

  const formActions = useMemo(
    () => ({
      size: 'md' as const,
      discard: {
        label: 'Discard',
        variant: 'outline' as const,
        handler: () => setIsEditMode(false),
      },
      submit: {
        label: 'Save Changes',
        isSubmitting: isPending,
        forceEnabled: true,
      },
    }),
    [isPending],
  );

  return (
    <Modal
      hideCloseButton={isEditMode}
      closeButton={<IoClose size={44} className="z-1000" />}
      className="scroll"
      scrollBehavior="outside"
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size="4xl"
      classNames={{
        header: 'text-hl! flex flex-col gap-4 text-base-20 font-secondary pt-6 pb-4',
        body: 'flex flex-col mx-6 p-0 pb-6 gap-6 bg-base-80',
        base: 'bg-base-80',
        footer: 'pb-6',
        closeButton:
          'mr-4 mt-6 outline-none focus:outline-none focus-visible:outline-none ring-0 focus:ring-0 focus-visible:ring-0',
      }}
    >
      <ChildForm<EditDonorProfileFormValues>
        defaultValues={defaultValues}
        schema={editDonorProfileSchema}
        onSubmit={handleSubmit}
      >
        <ModalContent>
          <ModalHeader>
            <div>OPO Clinical Request</div>
            <Divider />
          </ModalHeader>

          <ModalBody>
            {isEditMode ? (
              <DonorProfileForm organType={organType} donorType={donorType} />
            ) : (
              <OpoDonorDataView matchId={matchId} donorData={donorData} organType={organType} donorType={donorType} />
            )}
          </ModalBody>

          <ModalFooter>
            {isEditMode ? (
              <FormActions actions={formActions} />
            ) : (
              canEdit && (
                <Button
                  size="md"
                  onPress={(e) => {
                    focusClosestDialog(e.target);
                    setIsEditMode(true);
                  }}
                >
                  Edit Donor Information
                </Button>
              )
            )}
          </ModalFooter>
        </ModalContent>
      </ChildForm>
    </Modal>
  );
};
